import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { VehicleService } from 'src/app/modules/shared/services/vehicle.service';
import { VehicleManagementComponent } from '../vehicle-management.component';
import * as fromActions from 'src/app/modules/store/vehicle/vehicle.actions';
import * as fromSelectors from 'src/app/modules/store/vehicle/vehicle.selectors';
import { VehiclesState } from 'src/app/modules/store/vehicle/vehicle.reducer';
import { ActionsSubject, Store } from '@ngrx/store';
import { VehicleImage } from 'src/app/modules/shared/models/vehicle-image';
import { ITestDriveVehicleInventory, SourceSystem } from '@signal/asp-data-commons';
import { SubscriptionList } from 'src/app/modules/shared/models/asp.types';
import { unsubscribeSubscriptions } from 'src/app/modules/shared/services/util.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-add-vehicle-form',
  templateUrl: './add-vehicle-form.component.html',
  styleUrls: ['./add-vehicle-form.component.scss']
})
export class AddVehicleFormComponent implements OnInit, OnDestroy {
  vehicleForm: UntypedFormGroup;
  isInvalidVin: boolean = false;
  isFetchingData: boolean = false;
  vehicle: ITestDriveVehicleInventory;
  subscriptions: SubscriptionList = {};
  previousVin : string = '';
  isVinExist : boolean = false;
  allVehicles = [];
  clickedOutside=false;
  isSubmitting = false;
  @ViewChild('form') form: ElementRef;
  
  constructor(private readonly dialogRef: MatDialogRef<VehicleManagementComponent>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly vehicleService: VehicleService,
    private readonly vehicleStore: Store<VehiclesState>,
    private readonly actionsSubject: ActionsSubject,
    @Inject(MAT_DIALOG_DATA) public readonly data: any
  ) { }

  ngOnInit(): void {
    this.subscriptions.allVehicle = this.vehicleStore.select(fromSelectors.selectAllVehiclesFromInventory).subscribe(data=>{
      if(data && data.length) {
        this.allVehicles = data;
      }
    });
    this.subscriptions.addVehicle = this.actionsSubject.pipe(
      filter(action => action.type === fromActions.VehiclesActionTypes.ADD_VEHICLE_SUCCESS || action.type === fromActions.VehiclesActionTypes.ADD_VEHICLE_FAILURE)
    ).subscribe(_action => {
      if (!this.isSubmitting) {
        return;
      }

      this.isSubmitting = false;
      this.close();
    });
    this.InitializeForm();
  }

  @HostListener("document:click", ['$event.target'])
  showMessage(event)
  {    
    if (!this.form.nativeElement.contains(event) )
    {
      if(this.data.clicked) {
        this.data.clicked=false;
      }
      else{
        this.clickedOutside=true;
      }
    }
  }

  InitializeForm() {
    this.vehicleForm = this.formBuilder.group({
      vin: ['', [Validators.required]],
      model: ['', [Validators.required]],
      make: ['', [Validators.required]],
      year: ['', [Validators.required]],
      trim: ['', [Validators.required]],
      exteriorColorCode: ['', [Validators.required]],
      exteriorColorName: [''],
      exteriorColorHexCode: [''],
      licensePlate: [''],
      status: [false],
      imageUrl: [''],
      marketingTitle: ['', [Validators.required]],
      marketingName: ['']
    })
    this.vehicleForm.controls.vin.setValidators(this.vinValidator());
    this.vehicleForm.controls.vin.updateValueAndValidity();
    this.onVinChanges();
  }
  clearData() {
    const vehicleData = {
      model: '',
      make: '',
      year: '',
      trim: '',
      exteriorColorName: '',
      exteriorColorCode: '',
      exteriorColorHexCode: '',
      licensePlate: '',
      status: false,
      imageUrl: '',
      marketingTitle: '',
      marketingName: ''
    }
    this.vehicleForm.patchValue(vehicleData);
  }

  onVinChanges(){
    this.subscriptions.vinSub = this.vehicleForm.get('vin').valueChanges.subscribe((data: string) => {
      if(data && data.length === 17 && this.VinField.valid && data!== this.previousVin) {
        this.isInvalidVin = false;
        this.isVinExist = this.allVehicles.findIndex(x=> x.vin === data) === -1 ? false : true;
        this.previousVin = data;
        if(!this.isVinExist){
          this.getVehicleDetails(data);
        }
      }
    })
  }

  getVehicleDetails(vin : string) {
    this.isFetchingData = true;
    this.clearData();
    this.subscriptions.vinData =this.vehicleService.getVehicleColorForLexus({ vin }, true).subscribe(result => {
      if (result.data?.length && result.data[0]?.model) {
        const vehicle = result.data[0];
        this.subscriptions.colorSub =this.vehicleService.getVehicleColors(vehicle.make.toUpperCase().substr(0, 3),
          vehicle.year.toString(), vehicle.model.replace(' ', '').toLowerCase(),
          vehicle.trim).subscribe((data: VehicleImage) => {
            const color = data.colors.exterior.find(x => x.code === vehicle.exteriorColor);
          
            this.vehicle = { ...vehicle, exteriorColorHexCode: `#${color.hex_value[0]}`, exteriorColorName: color.title, exteriorColorCode: vehicle.exteriorColor }
            this.patchFormValue();
          }, err => {
            this.vehicle = { ...vehicle, exteriorColorHexCode: '', exteriorColorName: '', exteriorColorCode: vehicle.exteriorColor }
            this.patchFormValue();
          })
      }
      else {
        this.isFetchingData = false;
        this.isInvalidVin = true;
      }
    })
  }

  patchFormValue() {
    this.isFetchingData = false;
    this.isInvalidVin = false;
    this.vehicleForm.patchValue(this.vehicle);
  }

  get VinField() {
    return this.vehicleForm.get('vin');
  }

  validField(control: string){
    if(this.vehicle && !this.isInvalidVin && !this.vehicle[control]){
      return false;
    }
    else{
      return true;
    }
  }

  submitForm() {
    if (this.isSubmitting) {
      return;
    }

    if (this.vehicleForm.valid && this.vehicle?.vin === this.vehicleForm.value.vin) {
      const vehicleData = { ...this.vehicleForm.value, year: `${this.vehicleForm.value.year}`, sourceSystem : SourceSystem.ME }
      this.isSubmitting = true;
      this.vehicleStore.dispatch(new fromActions.AddVehicle({ vehicle: vehicleData }));
    }
  }

  updateSwitchValues(val: boolean) {
    this.vehicleForm.get('status').patchValue(val);
  }

  close() {
    this.dialogRef.close();
  }

  vinValidator(): ValidatorFn {
    return (control: UntypedFormControl): ValidationErrors => {
      const vin = this.vehicleForm.controls.vin;
      if (!vin.value) {
        return { required: 'service.vinIsRequired' };
      } else {
        if (!/^([a-zA-Z0-9]{17})$/.test(vin.value)) {
          return { pattern17DigitAndAlphaNumeric: 'service.vinShouldBeDigitAndAlphanumeric' };
        } else if (!/^(.{17})$/.test(vin.value)) {
          return { pattern17Digit: 'service.vinShouldBeDigit' };
        } else if (!/^([a-zA-Z0-9]*)$/.test(vin.value)) {
          return { patternAlphaNumeric: 'service.vinShouldBeAlphanumeric' };
        }
        else {
          return null;
        }
      }
    }
  }

  ngOnDestroy() {
    this.isInvalidVin = false;
    this.isFetchingData = false;
    unsubscribeSubscriptions(this.subscriptions);
  }
}
